import React, { FC, useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import LoadingModal from 'components/generalUI/LoadingModal';
import { useParkScheduleQuery } from 'apollo/resolvers/parkAvailability/list';
import useListPacks from 'apollo/resolvers/packs/list';
import EntranceCard from 'components/TicketSelection/EntranceCard';
import PurchaseSteps from 'components/generalUI/PurchaseSteps';
import useListEntranceTicketTypesQuery from 'apollo/resolvers/entranceTicketTypes/list';
import { EntranceTicketType, WithFeatures } from 'apollo/types';
import styles from './TicketSelection.module.scss';

const TicketSelection: FC = () => {
  const { t } = useTranslation();
  const { data: packsData, loading: loadingPacks } = useListPacks();
  const { data: entranceTicketTypesData, loading: loadingEntranceTicketTypes } =
    useListEntranceTicketTypesQuery();
  const {
    summerSchedule,
    winterSchedule,
    loading: loadingParkSchedule,
  } = useParkScheduleQuery();

  const [entranceTicketTypes, setEntranceTicketTypes] = useState<
    WithFeatures<EntranceTicketType>[]
  >([]);

  const sortEntranceTicketTypesForPurchaseByIsBicycle = () => {
    const isBicycleTickets = entranceTicketTypesData?.listEntranceTicketTypes.filter(
      (ticket) => ticket.isBicycle
    );

    const isNotBicycleTickets = entranceTicketTypesData?.listEntranceTicketTypes.filter(
      (ticket) => !ticket.isBicycle
    );

    return isNotBicycleTickets?.concat(isBicycleTickets || []) || [];
  };

  useEffect(() => {
    const sorted = sortEntranceTicketTypesForPurchaseByIsBicycle();
    setEntranceTicketTypes(sorted);
  }, [entranceTicketTypesData]);

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        <PurchaseSteps total={4} current={0} />
      </div>
      <Row justify="center" align="middle">
        <Col span={24}>
          <h2 className={styles.Title}>{t('pages.ticketSelection.selectTicketType')}</h2>
        </Col>
      </Row>
      <Row justify="center" align="middle">
        <Col span={24}>
          {summerSchedule && winterSchedule && (
            <p className={`${styles.Description} ${styles.Schedule}`}>
              <b>{t('components.parkSchedule.summerMonths')}:</b>{' '}
              {t('components.parkSchedule.hours', {
                aperture: summerSchedule.aperture,
                closure: summerSchedule.closure,
              })}
              <span className={styles.ScheduleDivider} />
              <b>{t('components.parkSchedule.winterMonths')}:</b>{' '}
              {t('components.parkSchedule.hours', {
                aperture: winterSchedule.aperture,
                closure: winterSchedule.closure,
              })}
            </p>
          )}
          <p className={styles.Description}>
            {t('pages.ticketSelection.limitedTicketsNotice')}
          </p>
        </Col>
      </Row>
      <LoadingModal
        visible={loadingPacks || loadingEntranceTicketTypes || loadingParkSchedule}
      />
      <div className={styles.ScrollEntrances}>
        {packsData?.listPacks
          .filter((pack) => pack.visibility)
          .map((pack) => (
            <Col
              className={styles.Column}
              xs={16}
              sm={16}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
              key={pack.id}
            >
              <EntranceCard item={pack} isPack images={pack.packImages} />
            </Col>
          ))}
        {entranceTicketTypes
          .filter((entranceTicketType) => entranceTicketType.visibility)
          .map((entranceTicketType) => (
            <Col
              className={styles.Column}
              xs={16}
              sm={16}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
              key={entranceTicketType.id}
            >
              <EntranceCard
                item={entranceTicketType}
                isPack={false}
                images={entranceTicketType.entranceImages || []}
              />
            </Col>
          ))}
      </div>
    </div>
  );
};

export default TicketSelection;
